import { useState } from 'react';
import { PermalinkCell } from '../../../shared/ui/PermalinkCell';

import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

import { ContentTypeBadge } from './ContentTypeBadge';
import { CopyColumnButton } from './CopyColumnButton';
import { TablePagination } from './TablePagination';

import type { MediaStats, MediaStatsError, NumericStatsKey } from '../types/statistics';

const PAGE_SIZE = 50;

type StatsTableProps = {
  results: MediaStats[];
  errors: MediaStatsError[];
  originalUrls: string[];
  isLoading: boolean;
};

const INSIGHTS_ONLY_KEYS: Set<NumericStatsKey> = new Set([
  'impressionsInstagram',
  'impressionsFacebook',
  'impressionsTotal',
  'shares',
  'reach',
  'skipRate',
  'igReelsAvgWatchTime',
  'videoDuration',
]);

const NUMERIC_KEYS: NumericStatsKey[] = [
  'impressionsInstagram',
  'impressionsFacebook',
  'impressionsTotal',
  'likes',
  'comments',
  'shares',
  'reach',
  'skipRate',
  'igReelsAvgWatchTime',
  'videoDuration',
];

function formatDate(val: string | null): string {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatMetricValue(value: number, key: NumericStatsKey, isPartial: boolean): string {
  if (isPartial && INSIGHTS_ONLY_KEYS.has(key)) return '—';
  if (key === 'skipRate') return `${value}%`;
  if (key === 'igReelsAvgWatchTime') return `${(value / 1000).toFixed(1)} сек`;
  if (key === 'videoDuration') return `${value} сек`;
  return value.toLocaleString('ru-RU');
}

const NON_SUMMABLE: Set<NumericStatsKey> = new Set([
  'skipRate',
  'igReelsAvgWatchTime',
  'videoDuration',
]);

function computeTotals(data: MediaStats[]): Record<NumericStatsKey, number> {
  const totals: Record<NumericStatsKey, number> = {
    impressionsInstagram: 0,
    impressionsFacebook: 0,
    impressionsTotal: 0,
    likes: 0,
    comments: 0,
    shares: 0,
    reach: 0,
    skipRate: 0,
    igReelsAvgWatchTime: 0,
    videoDuration: 0,
  };

  for (const row of data) {
    for (const key of NUMERIC_KEYS) {
      if (!NON_SUMMABLE.has(key)) {
        totals[key] += row[key];
      }
    }
  }

  return totals;
}

/** Ссылки, по которым цифр нет: в таблицу и итоги они не входят, здесь — с причиной. */
function ExcludedLinks({ errors }: { errors: MediaStatsError[] }): JSX.Element {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
      <div className="mb-2 text-sm font-medium">
        Не включены в отчёт ({errors.length})
      </div>
      <ul className="flex flex-col gap-1.5 text-sm">
        {errors.map((e) => (
          <li key={e.url} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
            <a
              href={e.url}
              target="_blank"
              rel="noreferrer"
              className="break-all text-primary hover:underline sm:max-w-[45%]"
            >
              {e.url.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
            <span className="text-muted-foreground">{e.error}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StatsTable({
  results,
  errors,
  originalUrls,
  isLoading,
}: StatsTableProps): JSX.Element {
  const [page, setPage] = useState(1);

  // Instagram/Facebook impression breakdowns are meaningless for TikTok, so hide
  // those two columns when every result is a TikTok link.
  const allTikTok = results.length > 0 && results.every((r) => /tiktok\.com/i.test(r.url));

  if (isLoading) {
    return (
      <div className="bg-background rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ссылка</TableHead>
              <TableHead className="w-[110px]">Тип контента</TableHead>
              <TableHead className="w-[110px]">Дата публикации</TableHead>
              <TableHead className="w-[140px] text-right">Показы Instagram</TableHead>
              <TableHead className="w-[130px] text-right">Показы Facebook</TableHead>
              <TableHead className="w-[130px] text-right">Всего показов</TableHead>
              <TableHead className="w-[80px] text-right">Лайки</TableHead>
              <TableHead className="w-[120px] text-right">Комментарии</TableHead>
              <TableHead className="w-[90px] text-right">Репосты</TableHead>
              <TableHead className="w-[90px] text-right">Охваты</TableHead>
              <TableHead className="w-[90px] text-right">Реакция</TableHead>
              <TableHead className="w-[100px] text-right">Удержание</TableHead>
              <TableHead className="w-[130px] text-right">Продолжительность</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-10 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                <TableCell className="text-right"><Skeleton className="h-4 w-14 ml-auto" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (results.length === 0 && errors.length === 0) {
    return <></>;
  }

  const urlOrder = new Map(originalUrls.map((url, i) => [url, i]));
  const byInputOrder = (a: { url: string }, b: { url: string }) =>
    (urlOrder.get(a.url) ?? Infinity) - (urlOrder.get(b.url) ?? Infinity);
  const sortedResults = [...results].sort(byInputOrder);
  const sortedErrors = [...errors].sort(byInputOrder);

  const totals = computeTotals(results);
  const totalPages = Math.max(1, Math.ceil(sortedResults.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sortedResults.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const showPagination = totalPages > 1;

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-3">
        {sortedErrors.length > 0 && <ExcludedLinks errors={sortedErrors} />}

        {sortedResults.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)]">
              <TableRow className="hover:bg-background">
                <TableHead>Ссылка</TableHead>
                <TableHead className="w-[110px]">Тип контента</TableHead>
                <TableHead className="w-[110px]">Дата публикации</TableHead>
                {!allTikTok && (
                  <TableHead className="w-[140px]">
                    <div className="flex items-center justify-end">
                      Показы Instagram
                      <CopyColumnButton columnKey="impressionsInstagram" data={sortedResults} />
                    </div>
                  </TableHead>
                )}
                {!allTikTok && (
                  <TableHead className="w-[130px]">
                    <div className="flex items-center justify-end">
                      Показы Facebook
                      <CopyColumnButton columnKey="impressionsFacebook" data={sortedResults} />
                    </div>
                  </TableHead>
                )}
                <TableHead className="w-[130px]">
                  <div className="flex items-center justify-end">
                    Всего показов
                    <CopyColumnButton columnKey="impressionsTotal" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[80px]">
                  <div className="flex items-center justify-end">
                    Лайки
                    <CopyColumnButton columnKey="likes" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[120px]">
                  <div className="flex items-center justify-end">
                    Комментарии
                    <CopyColumnButton columnKey="comments" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[90px]">
                  <div className="flex items-center justify-end">
                    Репосты
                    <CopyColumnButton columnKey="shares" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[90px]">
                  <div className="flex items-center justify-end">
                    Охваты
                    <CopyColumnButton columnKey="reach" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[90px]">
                  <div className="flex items-center justify-end">
                    Реакция
                    <CopyColumnButton columnKey="skipRate" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[100px]">
                  <div className="flex items-center justify-end">
                    Удержание
                    <CopyColumnButton columnKey="igReelsAvgWatchTime" data={sortedResults} />
                  </div>
                </TableHead>
                <TableHead className="w-[130px]">
                  <div className="flex items-center justify-end">
                    Продолжительность
                    <CopyColumnButton columnKey="videoDuration" data={sortedResults} />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map(r => {
                return (
                  <TableRow key={r.shortcode}>
                    <TableCell>
                      <PermalinkCell url={r.url} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <ContentTypeBadge contentType={r.contentType} label={r.contentTypeLabel} />
                        {r.isPartial && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="text-xs text-muted-foreground cursor-default"
                              >
                                публ.
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[220px]">
                              Аккаунт не подключён — доступны только публичные метрики (лайки,
                              комментарии)
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground text-sm">
                      {formatDate(r.publishedAt)}
                    </TableCell>
                    {!allTikTok && (
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatMetricValue(r.impressionsInstagram, 'impressionsInstagram', r.isPartial)}
                      </TableCell>
                    )}
                    {!allTikTok && (
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatMetricValue(r.impressionsFacebook, 'impressionsFacebook', r.isPartial)}
                      </TableCell>
                    )}
                    <TableCell className="text-right font-medium tabular-nums text-muted-foreground">
                      {formatMetricValue(r.impressionsTotal, 'impressionsTotal', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMetricValue(r.likes, 'likes', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMetricValue(r.comments, 'comments', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatMetricValue(r.shares, 'shares', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatMetricValue(r.reach, 'reach', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatMetricValue(r.skipRate, 'skipRate', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatMetricValue(r.igReelsAvgWatchTime, 'igReelsAvgWatchTime', r.isPartial)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatMetricValue(r.videoDuration, 'videoDuration', r.isPartial)}
                    </TableCell>
                  </TableRow>
                );
              })}

              {results.length > 1 && (
                <TableRow className="font-semibold bg-muted/50">
                  <TableCell colSpan={3} className="text-gray-600">
                    Итого ({results.length})
                  </TableCell>
                  {!allTikTok && (
                    <TableCell className="text-right tabular-nums">
                      {totals.impressionsInstagram.toLocaleString('ru-RU')}
                    </TableCell>
                  )}
                  {!allTikTok && (
                    <TableCell className="text-right tabular-nums">
                      {totals.impressionsFacebook.toLocaleString('ru-RU')}
                    </TableCell>
                  )}
                  <TableCell className="text-right tabular-nums">
                    {totals.impressionsTotal.toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.likes.toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.comments.toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.shares.toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {totals.reach.toLocaleString('ru-RU')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        )}

        {showPagination && (
          <TablePagination page={safePage} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>
    </TooltipProvider>
  );
}
